import { useState } from "react";
import { toast } from "sonner";
import { Search, Loader2, Upload, Save } from "lucide-react";
import MmpLayout from "./MmpLayout";
import api from "@/lib/api";

export default function MmpRetrieve() {
  const [query, setQuery] = useState("");
  const [prof, setProf] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [files, setFiles] = useState({ cv: null, photo: null, portfolio: null });

  const on = (k) => (e) => setProf({ ...prof, [k]: e.target.value });
  const onFile = (k) => (e) => setFiles((f) => ({ ...f, [k]: e.target.files?.[0] || null }));

  const search = async (e) => {
    e?.preventDefault();
    if (!query.trim()) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/profiles/retrieve?search=${encodeURIComponent(query.trim())}`);
      setProf(data);
      setFiles({ cv: null, photo: null, portfolio: null });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Profile not found");
      setProf(null);
    } finally { setLoading(false); }
  };

  const save = async () => {
    if (!prof) return;
    setSaving(true);
    try {
      const fd = new FormData();
      const { cv_path, passport_photo_path, portfolio_path, id: _id, ...rest } = prof;
      const payload = { ...rest, unique_id: prof.unique_id, age: Number(prof.age) };
      fd.append("payload", JSON.stringify(payload));
      // Backend /profiles/submit only updates uploaded files; text fields will remain unchanged unless we replace files.
      // For text-only updates when no files supplied, we send empty files header? The endpoint requires cv+photo.
      // Simpler: only trigger the combined endpoint when files are being replaced; text-only edits currently rely on files present.
      if (files.cv) fd.append("cv", files.cv);
      if (files.photo) fd.append("photo", files.photo);
      if (files.portfolio) fd.append("portfolio", files.portfolio);
      // If any file is missing, use per-file upload endpoints for the replacements; text-only save is not supported by the current combined endpoint.
      if (files.cv || files.photo || files.portfolio) {
        // Use per-endpoint uploads (text is not required by these — no need to resend text on file replacement)
        const cfg = { headers: { "Content-Type": "multipart/form-data" }, timeout: 120000 };
        if (files.cv) { const f = new FormData(); f.append("file", files.cv); await api.post(`/profiles/upload-cv/${prof.unique_id}`, f, cfg); }
        if (files.photo) { const f = new FormData(); f.append("file", files.photo); await api.post(`/profiles/upload-photo/${prof.unique_id}`, f, cfg); }
        if (files.portfolio) { const f = new FormData(); f.append("file", files.portfolio); await api.post(`/profiles/upload-portfolio/${prof.unique_id}`, f, cfg); }
      }
      toast.success("Profile updated");
      await search();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Update failed");
    } finally { setSaving(false); }
  };

  return (
    <MmpLayout>
      <div className="mia-container py-10 lg:py-14 max-w-3xl">
        <h1 className="font-display font-black text-3xl lg:text-4xl tracking-tight">Retrieve / Update Profile</h1>
        <p className="text-slate-400 mt-2">Enter your email or Unique ID (e.g. MMP-2026-000124) to view or replace your submitted files.</p>

        <form onSubmit={search} className="mt-8 flex gap-3">
          <div className="flex-1 rounded-lg border border-white/10 bg-[#0E1525] px-4 py-3 flex items-center gap-3 focus-within:border-sky-500">
            <Search className="w-4 h-4 text-slate-500" />
            <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Email or MMP-2026-XXXXXX" className="w-full bg-transparent focus:outline-none text-sm text-slate-100" data-testid="mmp-retrieve-input" />
          </div>
          <button type="submit" disabled={loading} className="bg-sky-500 hover:bg-sky-400 text-[#0B0F1A] font-bold text-sm uppercase tracking-widest px-6 rounded-lg disabled:opacity-60" data-testid="mmp-retrieve-search">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Search"}
          </button>
        </form>

        {prof && (
          <div className="mt-8 space-y-6" data-testid="mmp-retrieve-result">
            <div className="rounded-2xl border border-white/10 bg-[#0E1525] p-6">
              <div className="flex items-center justify-between gap-4 flex-wrap">
                <div>
                  <div className="text-[0.62rem] font-bold tracking-[0.24em] uppercase text-sky-400">Your Profile</div>
                  <div className="font-mono text-lg font-black mt-1">{prof.unique_id}</div>
                </div>
                <span className={`text-[10px] px-2.5 py-1 rounded-full uppercase font-bold tracking-wider ${prof.status === "Shortlisted" ? "bg-emerald-500/20 text-emerald-300" : "bg-sky-500/20 text-sky-300"}`}>{prof.status}</span>
              </div>
              <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="Full Name" value={prof.full_name} onChange={on("full_name")} />
                <Field label="Email" value={prof.email} disabled />
                <Field label="Phone" value={prof.phone} onChange={on("phone")} />
                <Field label="Profession" value={prof.profession} disabled />
                <Field label="Experience" value={prof.experience} onChange={on("experience")} />
                <Field label="Skills" value={prof.skills || ""} onChange={on("skills")} />
                <Field label="Country" value={prof.country} disabled />
                <Field label="State" value={prof.state} disabled />
                <Field label="Home Address" value={prof.address} onChange={on("address")} full />
              </div>
            </div>

            <div className="rounded-2xl border border-white/10 bg-[#0E1525] p-6 space-y-4">
              <div className="text-[0.62rem] font-bold tracking-[0.24em] uppercase text-sky-400">Replace Documents (optional)</div>
              <ReplaceRow label="Passport Photograph" accept="image/*" file={files.photo} onChange={onFile("photo")} current={prof.passport_photo_path} testid="mmp-replace-photo" />
              <ReplaceRow label="CV (PDF)" accept="application/pdf" file={files.cv} onChange={onFile("cv")} current={prof.cv_path} testid="mmp-replace-cv" />
              <ReplaceRow label="Portfolio" accept="application/pdf,image/*" file={files.portfolio} onChange={onFile("portfolio")} current={prof.portfolio_path} testid="mmp-replace-portfolio" />
            </div>

            <button onClick={save} disabled={saving || !(files.cv || files.photo || files.portfolio)} className="w-full inline-flex items-center justify-center gap-2 bg-sky-500 hover:bg-sky-400 disabled:opacity-40 text-[#0B0F1A] font-bold text-sm uppercase tracking-[0.14em] px-6 py-4 rounded-lg" data-testid="mmp-retrieve-save">
              {saving ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving…</> : <><Save className="w-4 h-4" /> Save Changes</>}
            </button>
            <p className="text-xs text-slate-500 text-center">Only file replacements are saved from this page. To edit locked fields (email, profession, country/state), contact Marine Infrastructure.</p>
          </div>
        )}
      </div>
    </MmpLayout>
  );
}

function Field({ label, value, onChange, disabled, full }) {
  return (
    <div className={full ? "sm:col-span-2" : ""}>
      <label className="text-xs font-semibold uppercase tracking-wider text-slate-400">{label}</label>
      <input value={value || ""} onChange={onChange} disabled={disabled} className={`mt-2 w-full rounded-lg border border-white/10 bg-[#0B0F1A] px-4 py-3 text-sm text-slate-100 focus:outline-none focus:border-sky-500 ${disabled ? "opacity-70" : ""}`} />
    </div>
  );
}

function ReplaceRow({ label, accept, file, onChange, current, testid }) {
  return (
    <div className="flex items-center gap-3 flex-wrap">
      <div className="flex-1 min-w-[180px]">
        <div className="text-sm text-slate-200 font-semibold">{label}</div>
        <div className="text-[0.65rem] text-slate-500 truncate">{file ? file.name : (current ? "On file · uploaded" : "Not uploaded")}</div>
      </div>
      <label className="inline-flex items-center gap-2 text-xs font-semibold text-sky-300 hover:text-sky-200 cursor-pointer">
        <Upload className="w-3.5 h-3.5" /> Choose file
        <input type="file" accept={accept} onChange={onChange} className="hidden" data-testid={testid} />
      </label>
    </div>
  );
}
